import { cn } from '@/lib/utils'
import { Check, Eye } from 'lucide-react'
import type { AgentTemplateListItem } from '@/schemas/agent-template'
import { getCategoryLabel, getCategoryColorClass } from '@/schemas/agent-template'
import { Button } from '@/components/ui/button'

interface TemplateCardProps {
  template: AgentTemplateListItem
  isSelected: boolean
  onSelect: () => void
  onPreview: () => void
}

export function TemplateCard({
  template,
  isSelected,
  onSelect,
  onPreview,
}: TemplateCardProps) {
  return (
    <div
      onClick={onSelect}
      className={cn(
        'relative group cursor-pointer rounded-xl p-4 transition-all duration-200',
        'border-2',
        isSelected
          ? 'border-indigo-500 bg-indigo-500/10 shadow-lg shadow-indigo-500/20'
          : 'border-gray-700/50 bg-white/5 hover:border-gray-600 hover:bg-white/10'
      )}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-white shadow-lg">
          <Check className="h-4 w-4" />
        </div>
      )}

      {/* Icon and Category */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-2xl">
          {template.icon}
        </div>
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
            getCategoryColorClass(template.category)
          )}
        >
          {getCategoryLabel(template.category)}
        </span>
      </div>

      {/* Name and Description */}
      <h3 className="font-medium text-white mb-1 truncate">{template.name}</h3>
      <p className="text-xs text-gray-400 line-clamp-2 min-h-[2.5rem]">
        {template.description || 'Sem descricao'}
      </p>

      {/* Preview button - appears on hover */}
      <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            onPreview()
          }}
          className="w-full text-xs text-gray-400 hover:text-white hover:bg-white/10"
        >
          <Eye className="h-3 w-3 mr-1" />
          Ver detalhes
        </Button>
      </div>
    </div>
  )
}
